import { apiSlice } from './apiSlice'

export enum CourtroomStatus {
  SCHEDULED = 'scheduled',
  ACTIVE = 'active',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
}

export interface Courtroom {
  id: string
  round_id: string
  case_id: string
  meeting_link: string
  meeting_id?: string
  status: CourtroomStatus
  participants: string[]
  attendance: Record<string, string> // user_id -> join_time
  recording_url?: string
  created_at?: string
  updated_at?: string
  started_at?: string
  ended_at?: string
}

export interface CourtroomCreate {
  round_id: string
  auto_create_meeting?: boolean
  meeting_title?: string
  start_time?: string
  end_time?: string
}

export const courtroomsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getCourtroom: builder.query<Courtroom, string>({
      query: (courtroomId) => `/courtrooms/${courtroomId}`,
      providesTags: (result, error, id) => [{ type: 'Courtroom', id }],
    }),
    getCourtroomByRound: builder.query<Courtroom, string>({
      query: (roundId) => `/courtrooms/round/${roundId}`,
      providesTags: (result, error, roundId) => [
        { type: 'Courtroom' },
        { type: 'Round', id: roundId },
      ],
    }),
    createCourtroom: builder.mutation<Courtroom, CourtroomCreate>({
      query: (body) => ({
        url: '/courtrooms/create',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Courtroom', 'Round'],
    }),
    joinCourtroom: builder.mutation<
      { message: string; courtroom_id: string; meeting_link: string },
      string
    >({
      query: (courtroomId) => ({
        url: `/courtrooms/${courtroomId}/join`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'Courtroom', id }],
    }),
    endCourtroom: builder.mutation<
      { message: string; courtroom: Courtroom },
      string
    >({
      query: (courtroomId) => ({
        url: `/courtrooms/${courtroomId}/end`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'Courtroom', id }],
    }),
    setRecording: builder.mutation<
      { message: string; recording_url: string },
      { courtroomId: string; recordingUrl: string }
    >({
      query: ({ courtroomId, recordingUrl }) => ({
        url: `/courtrooms/${courtroomId}/recording`,
        method: 'PUT',
        body: { recording_url: recordingUrl },
      }),
      invalidatesTags: (result, error, { courtroomId }) => [
        { type: 'Courtroom', id: courtroomId },
      ],
    }),
    getRecording: builder.query<{ recording_url: string }, string>({
      query: (courtroomId) => `/courtrooms/${courtroomId}/recording`,
      providesTags: (result, error, id) => [{ type: 'Courtroom', id }],
    }),
  }),
})

export const {
  useGetCourtroomQuery,
  useGetCourtroomByRoundQuery,
  useCreateCourtroomMutation,
  useJoinCourtroomMutation,
  useEndCourtroomMutation,
  useSetRecordingMutation,
  useGetRecordingQuery,
} = courtroomsApi
